import axios from 'axios';
import { getAuthHeader } from './otpAuthService';

const REST_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const BASE = `${REST_BASE}/takata`;

const auth = () => ({ headers: getAuthHeader() as any });

export type EsitoTakata =
  | 'non_coinvolto' | 'altri_richiami' | 'richiamo' | 'stop_drive'
  | 'non_soggetto' | 'fonte_non_disponibile';

export interface VerificaTakata {
  _id: string;
  protocollo: string;
  vin: string;
  marca: string;
  modello?: string;
  targa?: string;
  esito: EsitoTakata;
  codiceCampagna?: string;
  modalita: 'automatica' | 'guidata_manuale';
  numeroPraticaSTA?: string;
  corrispettivo?: { importo: number; ivaInclusa: boolean };
  datiAcquirente?: any;
  screenshotProva?: { filename: string };
  // Chi ha eseguito la verifica: il titolare (nome del legale rappresentante) o il dipendente.
  operatore?: string;
  dipendente?: string | null;
  createdAt: string;
}

export interface VerifyResponse {
  success: boolean;
  done?: boolean;
  mode?: 'guided' | 'needs_captcha' | 'non_disponibile' | 'marca_sconosciuta' | 'esclusa';
  marca?: string;
  wmi?: string;
  vin?: string;
  portaleUrl?: string;
  mitUrl?: string;
  fonti?: any[];
  verifica?: VerificaTakata;
  message?: string;
}

// `numeroPraticaSTA`: indicato dall'operatore prima di partire, viaggia fino al documento
// (compare sul certificato e davanti al nome del file scaricato).
export async function verify(vin: string, marca?: string, numeroPraticaSTA?: string): Promise<VerifyResponse> {
  const { data } = await axios.post(`${BASE}/verify`, { vin, marca, numeroPraticaSTA }, auth());
  return data;
}

// Numero di pratica e corrispettivo viaggiano anche di qui: la verifica nasce alla fine
// del percorso captcha, e senza portarli fin qui quello che l'operatore ha scritto nel
// modulo si perdeva proprio dove non c'era modo di accorgersene — il backend li accetta
// da sempre, era il sito a non mandarli.
export async function relayStart(vin: string, marca: string, numeroPraticaSTA?: string, corrispettivo?: string) {
  const { data } = await axios.post(`${BASE}/relay/start`, { vin, marca, numeroPraticaSTA, corrispettivo }, auth());
  return data as { success: boolean; relayId: string; captchaImage: string; message?: string };
}

export async function relaySubmit(relayId: string, captcha: string) {
  const { data } = await axios.post(`${BASE}/relay/submit`, { relayId, captcha }, auth());
  return data as { success: boolean; done?: boolean; retry?: boolean; captchaImage?: string; verifica?: VerificaTakata; message?: string };
}

export async function guidedInfo(marca: string) {
  const { data } = await axios.get(`${BASE}/guided-info`, { params: { marca }, ...auth() });
  return data as { success: boolean; mode: string; portaleUrl?: string; mitUrl?: string; fonti?: any[] };
}

export async function verifyManual(payload: {
  vin: string; marca: string; esito: EsitoTakata; codiceCampagna?: string;
  numeroPraticaSTA?: string; screenshot?: File | null;
}) {
  const fd = new FormData();
  fd.append('vin', payload.vin);
  fd.append('marca', payload.marca);
  fd.append('esito', payload.esito);
  if (payload.codiceCampagna) fd.append('codiceCampagna', payload.codiceCampagna);
  if (payload.numeroPraticaSTA) fd.append('numeroPraticaSTA', payload.numeroPraticaSTA);
  if (payload.screenshot) fd.append('screenshot', payload.screenshot);
  const { data } = await axios.post(`${BASE}/verify-manual`, fd, {
    headers: { ...(getAuthHeader() as any), 'Content-Type': 'multipart/form-data' },
  });
  return data as VerifyResponse;
}

// Il modulo di verifica sul sito è riservato a chi deve provare i portali: gli associati
// lavorano con l'estensione. Chi non è abilitato viene rimandato al registro.
export async function getAccessoModuloWeb() {
  const { data } = await axios.get(`${BASE}/accesso-modulo-web`, auth());
  return data as { success: boolean; consentito: boolean };
}

export async function getRegistro(params: { page?: number; esito?: string; search?: string } = {}) {
  const { data } = await axios.get(`${BASE}/registro`, { params, ...auth() });
  return data as { success: boolean; items: VerificaTakata[]; total: number; page: number; totalPages: number };
}

export async function getVerifica(id: string) {
  const { data } = await axios.get(`${BASE}/registro/${id}`, auth());
  return data as { success: boolean; verifica: VerificaTakata };
}

export async function salvaDatiAcquirente(id: string, payload: {
  datiAcquirente?: any; numeroPraticaSTA?: string; corrispettivo?: { importo: number; ivaInclusa: boolean };
}) {
  const { data } = await axios.post(`${BASE}/${id}/dati-acquirente`, payload, auth());
  return data as { success: boolean; verifica: VerificaTakata };
}

/**
 * Nome del file dall'intestazione Content-Disposition della risposta.
 * Il documento si scarica come blob, quindi il nome lo decide QUESTO codice: senza
 * leggerlo dalla risposta, il numero di pratica che il server antepone verrebbe ignorato.
 * L'intestazione è leggibile perché sito e API stanno sullo stesso dominio; in sviluppo
 * (porte diverse) può non esserlo, e allora vale il nome di ripiego.
 */
function nomeDaRisposta(headers: any): string {
  const cd = String(headers?.['content-disposition'] || headers?.['Content-Disposition'] || '');
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (!m || !m[1]) return '';
  try {
    return decodeURIComponent(m[1]).trim();
  } catch {
    return m[1].trim();
  }
}

async function downloadPdf(url: string, ripiego: string) {
  const response = await axios.get(url, { responseType: 'blob', ...auth() });
  const href = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = href;
  link.setAttribute('download', nomeDaRisposta(response.headers) || ripiego);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
}

// Il ripiego riproduce la stessa regola del server (pratica davanti al nome): serve solo
// se l'intestazione non è leggibile, e in quel caso è meglio di un nome senza pratica.
function conPratica(v: VerificaTakata, base: string) {
  const pratica = String(v.numeroPraticaSTA || '').replace(/[\\/:*?"<>|]+/g, '').trim();
  return pratica ? `${pratica} ${base}` : base;
}

export async function scaricaCertificato(v: VerificaTakata) {
  await downloadPdf(`${BASE}/${v._id}/certificato.pdf`, conPratica(v, `Certificato-Takata-${v.vin}.pdf`));
}

export async function scaricaDichiarazione(v: VerificaTakata) {
  await downloadPdf(`${BASE}/${v._id}/dichiarazione.pdf`, conPratica(v, `Dichiarazione-Takata-${v.vin}.pdf`));
}

// ---- Raccolta telai (fase di taratura) ----
export interface RaccoltaCounts { [marca: string]: { coinvolto: number; non_coinvolto: number } }
export interface TelaioRaccolto {
  _id: string; marca: string; vin: string; esito: 'coinvolto' | 'non_coinvolto'; nota?: string; createdAt: string;
}

export async function getRaccolta() {
  const { data } = await axios.get(`${BASE}/raccolta`, auth());
  return data as { success: boolean; counts: RaccoltaCounts; totale: number; mie: TelaioRaccolto[] };
}

export async function addRaccolta(payload: { marca: string; vin: string; esito: 'coinvolto' | 'non_coinvolto'; nota?: string }) {
  const { data } = await axios.post(`${BASE}/raccolta`, payload, auth());
  return data as { success: boolean; raccolto: TelaioRaccolto };
}

export async function deleteRaccolta(id: string) {
  const { data } = await axios.delete(`${BASE}/raccolta/${id}`, auth());
  return data as { success: boolean };
}

// ---- Stato dei portali + segnalazioni ----
export interface RicettaPortale {
  propria: boolean;
  generica: boolean;
  file?: string;
  livello?: string | null;
  validato: boolean;
  captcha?: string | null;
  dominio?: string | null;
}

export interface UltimoTestPortale {
  esito: 'ok' | 'non_funziona' | null;
  nota?: string;
  eseguitoDa?: string;
  data?: string | null;
}

export interface StatoPortale {
  marca: string;
  chiave: string;
  coinvolta: boolean;
  modo: string;
  mode: 'auto' | 'guided' | 'esclusa' | 'non_disponibile' | string;
  adapter?: string | null;
  portaleUrl?: string | null;
  mitUrl?: string | null;
  ricetta: RicettaPortale | null;
  // true quando la voce si appoggia alla ricetta generica: livello e captcha
  // sono ignoti (arrivano a null) e la pagina non deve affermare nulla su di essi.
  ricettaGenerica: boolean;
  livello?: string | null;
  validato: boolean;
  captcha?: string | null;
  verifiche30gg: number;
  ultimaVerifica?: string | null;
  ultimoTest: UltimoTestPortale | null;
  segnalazioniAperte: number;
}

export type TipoSegnalazione =
  | 'non_apre' | 'telaio_non_compilato' | 'esito_non_letto'
  | 'esito_sbagliato' | 'captcha' | 'altro';

export type StatoSegnalazione = 'aperta' | 'presa_in_carico' | 'risolta';

export interface SegnalazioneTakata {
  _id: string;
  marca: string;
  tipo: TipoSegnalazione;
  descrizione?: string;
  vin?: string;
  esitoAtteso?: string;
  esitoRegistrato?: string;
  operatore?: string;
  stato: StatoSegnalazione;
  notaInterna?: string;
  createdAt: string;
  updatedAt?: string;
}

export async function getStatoPortali() {
  const { data } = await axios.get(`${BASE}/stato-portali`, auth());
  return data as {
    success: boolean;
    portali: StatoPortale[];
    avvisi: string[];
    tipiSegnalazione: TipoSegnalazione[];
    nota: string;
  };
}

export async function inviaSegnalazione(payload: {
  marca: string; tipo: TipoSegnalazione; descrizione?: string; vin?: string;
  esitoAtteso?: string; esitoRegistrato?: string;
}) {
  const { data } = await axios.post(`${BASE}/segnalazioni`, payload, auth());
  return data as { success: boolean; segnalazione: SegnalazioneTakata };
}

export async function getMieSegnalazioni() {
  const { data } = await axios.get(`${BASE}/segnalazioni/mie`, auth());
  return data as { success: boolean; items: SegnalazioneTakata[] };
}

export const TIPO_SEGNALAZIONE_LABEL: Record<TipoSegnalazione, string> = {
  non_apre: 'Il portale non si apre o va in errore',
  telaio_non_compilato: 'Il telaio non viene compilato',
  esito_non_letto: 'La pagina risponde ma l\'esito non viene letto',
  esito_sbagliato: 'Esito registrato diverso da quello mostrato',
  captcha: 'Captcha bloccante o irrisolvibile',
  altro: 'Altro',
};

// Etichette che la capitalizzazione ingenua sbaglierebbe (stesse del backend).
const ETICHETTE_MARCA: Record<string, string> = {
  bmw: 'BMW',
  ds: 'DS',
  ram: 'RAM',
  'mercedes-benz': 'Mercedes-Benz',
  'alfa romeo': 'Alfa Romeo',
  'land rover': 'Land Rover',
  'daimler truck': 'Daimler Truck',
  daf: 'DAF',
};

/** Da chiave minuscola a etichetta leggibile. Tollera i valori gia' capitalizzati. */
export function etichettaMarcaTakata(marca?: string | null): string {
  const k = (marca || '').trim().toLowerCase();
  if (!k) return '';
  if (ETICHETTE_MARCA[k]) return ETICHETTE_MARCA[k];
  return k.split(' ').map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ');
}

// Marche su cui si puo' aprire una segnalazione (e che si scelgono nella verifica
// multipla): le coinvolte dell'elenco del Ministero piu' le NON SOGGETTE — le 4 escluse
// dal richiamo (Fiat, Alfa Romeo, Abarth, Maserati) e le fuori elenco che il telaio
// riconosce (Renault, Dacia, Kia, Hyundai, Volvo, Smart, Porsche). Stesso elenco di
// MARCHE_TAKATA + MARCHE_ESCLUSE in backend/utils/takata/wmiRegistry.js.
// `chiave` e' la forma minuscola con cui la marca viene SALVATA e filtrata: e' quella
// da mandare al server, l'etichetta serve solo a leggere.
// (Dichiarato DOPO etichettaMarcaTakata: la mappa delle etichette e' un const e verrebbe
// letta in zona morta temporale se questo elenco fosse inizializzato prima.)
export const MARCHE_SEGNALABILI: Array<{ chiave: string; label: string }> = [
  'audi', 'bmw', 'chrysler', 'citroen', 'daf', 'daihatsu', 'daimler truck', 'dodge',
  'ds', 'ferrari', 'ford', 'honda', 'jaguar', 'jeep', 'lancia', 'land rover',
  'lexus', 'mazda', 'mercedes-benz', 'mitsubishi', 'nissan', 'opel', 'peugeot',
  'ram', 'seat', 'skoda', 'subaru', 'suzuki', 'tesla', 'toyota', 'volkswagen',
  'fiat', 'alfa romeo', 'abarth', 'maserati',
  'renault', 'dacia', 'kia', 'hyundai', 'volvo', 'smart', 'porsche',
].map((chiave) => ({ chiave, label: etichettaMarcaTakata(chiave) }));

export const STATO_SEGNALAZIONE_LABEL: Record<StatoSegnalazione, string> = {
  aperta: 'Aperta',
  presa_in_carico: 'Presa in carico',
  risolta: 'Risolta',
};

export const ESITO_LABEL: Record<EsitoTakata, string> = {
  non_coinvolto: 'Non coinvolto',
  altri_richiami: 'Non coinvolto (altri richiami)',
  richiamo: 'COINVOLTO',
  stop_drive: 'COINVOLTO - STOP DRIVE',
  non_soggetto: 'Marca non soggetta',
  fonte_non_disponibile: 'Fonte non disponibile',
};

// Marche fuori dall'elenco del Ministero (Fiat, Alfa Romeo, Abarth, Maserati, Renault,
// Dacia, Kia, Hyundai, Volvo, Smart, Porsche): la verifica resta nel registro, ma il
// certificato non serve e non si produce: basta l'esito (decisione del committente,
// 22/10/2026). La marca e' dedotta dal telaio e un modello rimarchiato potrebbe averne
// un'altra: per questo si rimanda alla carta di circolazione.
export const TESTO_NON_SOGGETTO = 'Marca non compresa nell\'elenco del Ministero: il veicolo non va sottoposto a verifica Takata. Non serve il certificato. Se la carta di circolazione riporta un\'altra marca, correggila e ripeti la verifica.';

export function haCertificato(esito: EsitoTakata | string | undefined) {
  return esito !== 'non_soggetto';
}

export function isCoinvolto(esito: EsitoTakata) {
  return esito === 'richiamo' || esito === 'stop_drive';
}

// ---------------------------------------------------------------------------
// Verifica multipla (lotti di telai) — /api/takata/lotti
// Design: docs/superpowers/specs/2026-10-22-takata-verifica-multipla-design.md

export type GruppoLotto = 'server' | 'automatico' | 'manuale' | 'non_disponibile';
export type StatoVoceLotto = 'in_attesa' | 'in_corso' | 'fatto' | 'errore' | 'saltato';

export interface VoceLotto {
  indice: number;
  vin: string;
  marca: string;
  gruppo: GruppoLotto;
  portaleUrl: string;
  stato: StatoVoceLotto;
  esito: EsitoTakata | '';
  // Dati minimi della verifica registrata: bastano a scaricare il certificato.
  verifica: Pick<VerificaTakata, '_id' | 'vin' | 'marca' | 'esito' | 'protocollo' | 'numeroPraticaSTA' | 'createdAt'> | null;
  messaggio: string;
  aggiornataIl: string;
}

export interface RiepilogoLotto { totale: number; fatti: number; coinvolti: number; daFare: number; problemi: number }

export interface LottoTakata {
  _id: string;
  createdAt: string;
  operatore?: string;
  voci: VoceLotto[];
  scartati: { nonValidi: Array<{ testo: string; motivo: string }>; doppioni: string[] };
  esecuzione: { attiva: boolean; gruppo: string; aggiornataIl: string | null };
  riepilogo: RiepilogoLotto;
}

export interface LottoInElenco {
  _id: string;
  createdAt: string;
  operatore?: string;
  esecuzione: LottoTakata['esecuzione'];
  riepilogo: RiepilogoLotto;
}

export async function creaLotto(testo: string) {
  const { data } = await axios.post(`${BASE}/lotti`, { testo }, auth());
  return data as { success: boolean; lotto: LottoTakata; oltreLimite: string[]; massimo: number; doppioni: string[] };
}

export async function getLotti() {
  const { data } = await axios.get(`${BASE}/lotti`, auth());
  return data as { success: boolean; items: LottoInElenco[] };
}

export async function getLotto(id: string) {
  const { data } = await axios.get(`${BASE}/lotti/${encodeURIComponent(id)}`, auth());
  return data as { success: boolean; lotto: LottoTakata };
}

export async function cambiaMarcaVoce(id: string, indice: number, marca: string) {
  const { data } = await axios.put(`${BASE}/lotti/${encodeURIComponent(id)}/voci/${indice}/marca`, { marca }, auth());
  return data as { success: boolean; lotto: LottoTakata };
}

export async function statoVoceLotto(id: string, indice: number, stato: Exclude<StatoVoceLotto, 'fatto'>, messaggio = '') {
  const { data } = await axios.post(`${BASE}/lotti/${encodeURIComponent(id)}/voci/${indice}/stato`, { stato, messaggio }, auth());
  return data as { success: boolean };
}

export async function eliminaLotto(id: string) {
  const { data } = await axios.delete(`${BASE}/lotti/${encodeURIComponent(id)}`, auth());
  return data as { success: boolean };
}

// Verifica di una voce del gruppo «server» (marche escluse e CADET): stesso /verify della
// verifica singola, con il riferimento alla voce. Il server la collega al lotto.
export async function verificaVoceServer(lottoId: string, voce: Pick<VoceLotto, 'indice' | 'vin' | 'marca'>): Promise<VerifyResponse> {
  const { data } = await axios.post(`${BASE}/verify`, {
    vin: voce.vin, marca: voce.marca || undefined, lottoId, indice: voce.indice,
  }, auth());
  return data;
}

// Chiude il giro lato server quando l'estensione non puo' farlo (assente, gia' ferma, o
// interrotta): senza, la pagina resterebbe ad aspettare un battito che non arriva piu'.
export async function spegniEsecuzioneLotto(id: string) {
  const { data } = await axios.post(`${BASE}/lotti/${encodeURIComponent(id)}/esecuzione`, { attiva: false }, auth());
  return data as { success: boolean };
}
